import React, { useState } from 'react';
import { DateTime } from 'luxon';
import { useSelector } from 'react-redux';
import Input from '../../theme/forms/Input';
import getSubdomainString from '../../../utils/getSubdomainString';
import formatters from '../../../utils/forms/formatters';
import isUserAdmin from '../../../utils/session/perms';
import ConfirmModal from '../../theme/modals/ConfirmModal';
import { updateOrCreateApplication } from '../../../store/calls/applicationData';

const getImprovementName = (id, improvements = []) => {
  if (!improvements || improvements.length === 0) return '';
  const improvement = improvements.find((i) => i.id == id);
  return improvement ? improvement.name : '';
};

function ProjectCard({ project = {}, onArchived }) {
  const system_improvements = useSelector(
    (state) => state.indicativeOptionData.improvements
  );

  // Local UI state for archive flow
  const [confirmRemoval, setConfirmRemoval] = useState(false);
  const [confirmRemovalPrompt, setConfirmRemovalPrompt] = useState(null);
  const [confirmRemovalID, setConfirmRemovalID] = useState(null);
  const [hidden, setHidden] = useState(false);          // hides the card
  const [archiving, setArchiving] = useState(false);    // disables actions while archiving

  if (!project || hidden) return null;

  const { created, data, estimate_groups } = project;
  const { address, project_name, improvements } = data || {};
  if (!address) return null;

  const createdFmt = DateTime.fromJSDate(new Date(created)).toFormat('D');
  const project_completed = (estimate_groups?.length || 0) > 0 || project.application_id;

  const from_subdomain =
    !!project.subdomain &&
    !['www', 'app', '', null, undefined, 'cleanfi', 'narwhal'].includes(project.subdomain) &&
    project.subdomain !== getSubdomainString();

  // Open confirmation modal
  const openDeleteConfirm = (applicationId, name) => {
    setConfirmRemoval(true);
    setConfirmRemovalID(applicationId);
    setConfirmRemovalPrompt(
      `Are you sure you want to archive "${name || 'this application'}"?`
    );
  };

  // Close confirmation modal
  const closeDeleteConfirm = () => {
    setConfirmRemoval(false);
    setConfirmRemovalPrompt(null);
    setConfirmRemovalID(null);
  };

  return (
    <div className="box box-my-project is-flex is-flex-direction-column flex-start" key={project.id}>
      {/* Archive confirmation */}
      <ConfirmModal
        type="warning"
        visible={confirmRemoval}
        prompt={confirmRemovalPrompt}
        onAccept={async () => {
          // Optimistic UI: hide immediately
          setArchiving(true);
          setHidden(true);

          try {
            await updateOrCreateApplication({
              id: confirmRemovalID,
              is_active: false,
            });
            // Inform parent to remove from its list (if provided)
            onArchived?.(confirmRemovalID);
          } catch (err) {
            // Rollback if API fails
            setHidden(false);
            setArchiving(false);
          } finally {
            closeDeleteConfirm();
          }
        }}
        onCancel={closeDeleteConfirm}
      />

      <div className="is-flex is-justify-content-space-between">
        <div>
        <p
          className={`user-title has-text-weight-semibold ${
            project?.type_of_project === "Commercial"
              ? "has-text-primary"
              : "has-text-success"
          }`}
        >
          {project?.type_of_project}
        </p>
          <h3><b>{project_name}</b></h3>
          <p>{address?.formatted_address}</p>
          <p className="user-title">
            <b>Created by: {project.user_first_name} {project.user_last_name}</b>
            {' '} - <a href={`mailto:${project.user_email}`}>{project.user_email}</a>
          </p>
          {from_subdomain && (
            <p className="user-title has-text-primary mt-3">
              Created on: <b>{project.subdomain}</b>
            </p>
          )}
        </div>

        <div>
          <div>{createdFmt}</div>
          {!!project.application_payment_amount && (
            <div className="has-text-success has-text-weight-semibold">
              PAID: ${project.application_payment_amount} on {project.payment_date}
            </div>
          )}
          {from_subdomain && <div className="has-text-weight-semibold">{project.subdomain}</div>}
        </div>
      </div>

      <hr />

      <div className="is-flex is-justify-content-space-between">
        <div>
          {improvements?.length > 0 && improvements.map((impr) => (
            <h6 key={impr.id} className="title is-6 mb-0 mt-2 improvements-items user-title">
              <span>{getImprovementName(impr.id, system_improvements)}:</span>
              <span>{impr.value ? ` ${formatters.USDCurrency.formatter(impr.value)}` : ' -'}</span>
            </h6>
          ))}
        </div>

        <div className="buttons">
          {isUserAdmin() && !!project.application_id && (
            <button
              className={`button is-danger m-0 mr-3 mb-2 ${archiving ? 'is-loading' : ''}`}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                openDeleteConfirm(project.application_id, project_name);
              }}
              disabled={archiving}
            >
              Archive
            </button>
          )}

          {!project_completed && (
            <button className="button is-danger m-0 mr-3 mb-2 disabled">
              Project Not Yet Complete
            </button>
          )}

          {project_completed && (
            <>
              {!!project.application_id && (
                <a href={`/application/${project.application_id}`} target="_blank" rel="noreferrer">
                  <button className="button is-primary m-0 mr-3 mb-2">
                    {project.is_biddesk ? 'BidDesk Submission' : 'View Application'}
                  </button>
                </a>
              )}

              <a href={`/project-estimate/${project.id}`} target="_blank" rel="noreferrer">
                <button className="button is-link m-0 mr-3 mb-2">Get Today&apos;s Estimates</button>
              </a>

              <Input
                inputClasses="mb-2"
                type="select"
                placeholder="View Past Estimate"
                options={(estimate_groups || []).map((d) => ({
                  value: d.id,
                  display: DateTime.fromISO(d.created).toFormat('ff'),
                }))}
                onChange={(id) => window.open(`/share-estimate/?g=${id}`, '_blank')}
              />
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default ProjectCard;